import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import translate from "../locales";
import { PROJECTS } from "../data/data";
import {
  store,
  survey,
  PROGRESS_KEY,
  VOTE_YES,
  VOTE_NO,
  projAnswerKey,
} from "../survey";
import OverviewCard from "./overview_card";
import Toast from "./toast";
import { BackArrowIcon } from "./icons";

function saveTreesponse() {
  store.save().catch((error) => Sentry.captureException(error));
}

// Reaching the overview marks the treesponse completed, so a returning voter
// who picks "Sessie hervatten" is taken straight back here.
function markCompletedAndSave() {
  store.setCompleted();
  saveTreesponse();
}

const PROJECTS_BY_ID = Object.fromEntries(PROJECTS.map((p) => [p.id, p]));

function readVotedIds() {
  return PROJECTS.filter(
    (project) => store.getAnswer(projAnswerKey(project.id)) === VOTE_YES,
  ).map((project) => project.id);
}

export default function Results() {
  const [votedIds, setVotedIds] = useState(readVotedIds);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    if (!store.getAnswer("visited_overview")) {
      store.setAnswer("visited_overview", true);
      window.fathom?.trackEvent("overview entered");
    }
    markCompletedAndSave();
  }, []);

  useEffect(() => {
    const id = store.subscribe(
      PROJECTS.map((project) => projAnswerKey(project.id)),
      () => setVotedIds(readVotedIds()),
    );
    return () => store.unsubscribe?.(id);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const voted = votedIds.map((id) => PROJECTS_BY_ID[id]).filter(Boolean);
  const hasVotes = voted.length > 0;

  const showToast = (config) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(config);
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  const handleRemove = (id) => {
    store.setAnswer(projAnswerKey(id), VOTE_NO);
    saveTreesponse();
    showToast({
      variant: "neutral",
      title: translate("results.toast.removed.title"),
      message: translate("results.toast.removed.message"),
    });
  };

  const handleBack = (e) => {
    e.preventDefault();
    survey.previousPage();
  };

  const handleRestart = (e) => {
    e.preventDefault();
    store.setAnswer(PROGRESS_KEY, 0);
    store.setAnswer("completed", false);
    saveTreesponse();
    survey.previousPage();
  };

  return (
    <section className="results">
      <div className="toast-container" aria-live="polite">
        {toast && (
          <Toast
            variant={toast.variant}
            title={toast.title}
            message={toast.message}
          />
        )}
      </div>
      <header className="results__header">
        <button
          type="button"
          className="button button--clear results__back"
          onClick={handleBack}
        >
          <BackArrowIcon />
          {translate("results.back")}
        </button>
        <h1 className="results__title">
          {translate(hasVotes ? "results.title" : "results.empty_title")}
        </h1>
        {hasVotes ? (
          <>
            <p className="results__description">
              {translate("results.description_1")}
            </p>
            <p className="results__description">
              {translate("results.description_2")}
            </p>
            <p className="results__description">
              {translate("results.final_vote_start")}{" "}
              <a
                className="results__link"
                href={translate("results.final_vote_url")}
                target="_blank"
                rel="noreferrer"
              >
                {translate("results.final_vote_link")}
              </a>
            </p>
          </>
        ) : (
          <>
            <p className="results__description">
              {translate("results.empty_description_1_start")}{" "}
              <button
                type="button"
                className="results__restart-link"
                onClick={handleRestart}
              >
                {translate("results.empty_description_1_link")}
              </button>{" "}
              {translate("results.empty_description_1_end")}
            </p>
            <p className="results__description">
              {translate("results.empty_description_2")}
            </p>
          </>
        )}
        <a
          className="button results__cta"
          href={translate("results.discover_url")}
          target="_blank"
          rel="noreferrer"
        >
          {translate("results.discover")}
        </a>
      </header>
      {hasVotes && (
        <ul className="results__grid">
          {voted.map((project) => (
            <li key={project.id} className="results__item">
              <OverviewCard project={project} onRemove={handleRemove} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
